import classNames from "classnames";
import { FC, useCallback, useEffect, useId, useMemo, useState } from "react";

import { ReactComponent as AntennaIcon } from "assets/images/common/antenna.svg";
import { ReactComponent as CertificateIcon } from "assets/images/common/document.svg";
import { ReactComponent as EditIcon } from "assets/images/common/edit.svg";
import { ReactComponent as HighBatteryIcon } from "assets/images/common/full-battery.svg";
import { ReactComponent as LowBatteryIcon } from "assets/images/common/low-battery.svg";
import { ReactComponent as NotFoundIcon } from "assets/images/common/not-found.svg";
import { ReactComponent as PlugIcon } from "assets/images/common/plug.svg";
import { ReactComponent as ReloadIcon } from "assets/images/common/reload.svg";
import { ReactComponent as RecalibrateIcon } from "assets/images/common/reset.svg";
import { ReactComponent as TrashIcon } from "assets/images/common/trash.svg";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import SearchInput from "components/SearchInput";
import Select from "components/Select";
import Table from "components/Table";

import { HEADERS, STAT_CARDS, STATUS_OPTIONS, TYPE_OPTIONS } from "./data";

import useDebounce from "hooks/useDebounce";
import useRealtime from "hooks/useRealtime";

import { CreateDevicePayload, DeviceStatistics, UpdateDevicePayload } from "entities/devices";
import { AssetsApi, DevicesApi } from "services/http";

import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  createDeviceRequest,
  getDeviceListRequest,
  resetCalibrationState,
  resetCreateDeviceState,
  resetRemoveDeviceState,
  resetUpdateDeviceState,
  updateDeviceRequest,
} from "store/slices/deviceSlice";
import { setModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { hideModal, showModal } from "store/slices/modalSlice";
import { getPortfolioDeviceStatisticsRequest, resetPortfolioDeviceStatistic } from "store/slices/portfolioSlice";

import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";

import {
  deriveCalibration,
  deriveStatus,
  formatAbsoluteDateTime,
  formatDateOnly,
  formatModel,
} from "views/Devices/List";

import { BaseOption, ModalType } from "entities/common";
import { DeviceFormValues } from "widgets/Modal/CreateDevice/Content";

import styles from "./style.module.scss";

const BATTERY_LOW_THRESHOLD = 15;

type PropTypes = {
  id: number;
  portfolio: BaseOption;
  company: BaseOption;
};

const PortfolioDevicesTab: FC<PropTypes> = ({ id, portfolio, company }) => {
  const callbackKey = useId();
  const dispatch = useAppDispatch();
  const { portfolioDeviceStatistic } = useAppSelector((store) => store.portfolios);
  const { items, meta, isLoading, lastCreatedDevEui, lastUpdatedDevEui, lastRemovedDevEui, lastCalibratedDevEui } =
    useAppSelector((store) => store.devices);

  const [showFilters, setShowFilters] = useState(true);
  const [assetValue, setAssetValue] = useState("");
  const [assetLabel, setAssetLabel] = useState("");
  const [typeValue, setTypeValue] = useState("");
  const [statusValue, setStatusValue] = useState("");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const handleClearFilters = () => {
    setSearch("");
    setAssetValue("");
    setAssetLabel("");
    setTypeValue("");
    setStatusValue("");
    setPage(1);
  };

  const handleSaveDevice = useCallback(
    (raw?: unknown) => {
      const values = raw as DeviceFormValues;

      const payload: CreateDevicePayload = {
        dev_eui: values.devEui,
        app_key: values.appKey,
        device_profile_key: values.deviceProfileKey,
        portfolio_id: Number(values.portfolioId),
        asset_id: Number(values.assetId),
        installation_location: values.installationLocation || null,
        reporting_interval_seconds: Number(values.reportingInterval),
        last_calibration_at: values.lastCalibrationDate,
        calibration_interval_months: Number(values.calibrationInterval),
      };
      dispatch(createDeviceRequest({ payload, certificate: values.certificate }));
    },
    [dispatch],
  );

  const handleUpdateDevice = useCallback(
    (devEui: string, raw?: unknown) => {
      const values = raw as DeviceFormValues;

      const body: UpdateDevicePayload = {
        asset_id: Number(values.assetId),
        installation_location: values.installationLocation || null,
        reporting_interval_seconds: Number(values.reportingInterval),
        calibration_interval_months: Number(values.calibrationInterval),
      };
      dispatch(updateDeviceRequest({ devEui, body }));
    },
    [dispatch],
  );

  const handleAddDevice = useCallback(() => {
    setModalCallbacks(callbackKey, {
      onSave: handleSaveDevice,
      onUpdate: handleUpdateDevice,
    });
    dispatch(
      showModal({
        modalType: ModalType.CREATE_DEVICE,
        modalProps: {
          mode: "create",
          callbackKey,
          initialData: {
            portfolioId: String(id),
            portfolioLabel: portfolio.label,
            companyId: company.value,
            companyLabel: company.label,
          },
        },
      }),
    );
  }, [callbackKey, dispatch, handleSaveDevice, handleUpdateDevice, id, portfolio, company]);

  const handleEditDevice = useCallback(
    async (id: number) => {
      const device = items.find((d) => d.id === id);
      if (!device) return;

      setModalCallbacks(callbackKey, {
        onSave: handleSaveDevice,
        onUpdate: handleUpdateDevice,
      });

      let appKey = "";
      try {
        const { data } = await DevicesApi.getCurrent(device.dev_eui);
        appKey = data.app_key ?? "";
      } catch {
        appKey = "";
      }

      const initialData: Partial<DeviceFormValues> = {
        companyId: device.company_id != null ? String(device.company_id) : "",
        companyLabel: device.company_name ?? "",
        portfolioId: device.portfolio_id != null ? String(device.portfolio_id) : "",
        portfolioLabel: device.portfolio_name ?? "",
        assetId: device.asset_id != null ? String(device.asset_id) : "",
        assetLabel: device.asset_name ?? "",
        installationLocation: device.installation_location ?? "",
        deviceProfileKey: device.device_profile_key,
        devEui: device.dev_eui,
        appKey,
        reportingInterval: device.reporting_interval_seconds ? String(device.reporting_interval_seconds) : "",
        calibrationInterval:
          device.calibration_interval_months != null ? String(device.calibration_interval_months) : "12",
      };

      dispatch(
        showModal({
          modalType: ModalType.CREATE_DEVICE,
          modalProps: { mode: "edit", id, callbackKey, initialData },
        }),
      );
    },
    [callbackKey, dispatch, handleSaveDevice, handleUpdateDevice, items],
  );

  const handleRemoveDevice = useCallback(
    (id: number) => {
      const device = items.find((d) => d.id === id);
      if (!device) return;
      dispatch(
        showModal({
          modalType: ModalType.REMOVE_DEVICE,
          modalProps: { name: device.device_name, devEui: device.dev_eui },
        }),
      );
    },
    [dispatch, items],
  );

  const handleRecalibrate = useCallback(
    (id: number) => {
      const device = items.find((d) => d.id === id);
      if (!device) return;

      const toDisplayDate = (iso?: string | null) => {
        if (!iso) return undefined;
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? undefined : formatDateOnly(d);
      };

      dispatch(
        showModal({
          modalType: ModalType.MARK_CALIBRATED,
          modalProps: {
            devEui: device.dev_eui,
            deviceInfo: {
              model: device.device_model ? formatModel(device.device_model) : device.device_model,
              eui: device.dev_eui,
              lastCalibration: toDisplayDate(device.last_calibration_at),
              nextCalibrationDue: toDisplayDate(device.next_calibration_due),
            },
          },
        }),
      );
    },
    [dispatch, items],
  );

  const handleAssetChange = (value: string, label: string) => {
    setAssetValue(value);
    setAssetLabel(label);
  };

  const handleAssetSearch = useCallback(
    async (search: string, page: number) => {
      try {
        const { data } = await AssetsApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: search || undefined,
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PORTFOLIO_ID]: id,
            [QUERY_PARAMS.PAGE_SIZE]: 50,
          }),
        );

        const options = [
          { value: "", label: "All assets" },
          ...(data?.items || []).map((item) => ({ value: String(item.id), label: item.name })),
        ];
        const hasMore = data?.total ? page * 50 < data.total : false;

        return { options, hasMore };
      } catch {
        return { options: [{ value: "", label: "All assets" }], hasMore: false };
      }
    },
    [id],
  );

  const handleRequestList = useCallback(
    (silent = false) => {
      dispatch(
        getDeviceListRequest(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.PORTFOLIO_ID]: id,
            [QUERY_PARAMS.STATUS]: statusValue,
            [QUERY_PARAMS.ASSET_ID]: assetValue,
            [QUERY_PARAMS.SEARCH]: debouncedSearch,
            [QUERY_PARAMS.DEVICE_MODEL]: typeValue,
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PAGE_SIZE]: 10,
          }),
          silent ? { silent: true } : undefined,
        ),
      );
    },
    [dispatch, id, statusValue, assetValue, debouncedSearch, typeValue, page],
  );

  const handleStatisticsRequest = useCallback(
    (silent = false) => {
      dispatch(
        getPortfolioDeviceStatisticsRequest(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.PORTFOLIO_ID]: id,
          }),
          silent ? { silent: true } : undefined,
        ),
      );
    },
    [dispatch, id],
  );

  const tableData = useMemo(
    () =>
      items.map((device) => {
        const status = deriveStatus(device.last_seen_at, device.reporting_interval_seconds);
        const statusVariant = status === "Online" ? "online" : status === "Offline" ? "offline" : "neverSeen";

        const lastSeen = device.last_seen_at ? new Date(device.last_seen_at) : null;
        const validLastSeen = lastSeen && !Number.isNaN(lastSeen.getTime()) ? lastSeen : null;

        const calibration = deriveCalibration(device);

        return {
          asset: <span className={styles.asset}>{device.asset_name}</span>,
          location: <span className={styles.location}>{device.installation_location}</span>,
          device: (
            <span className={styles.device}>
              <span className={styles.deviceName}>{device.device_name}</span>
              <span className={styles.deviceType}>{device.dev_eui}</span>
            </span>
          ),
          status: <span className={classNames(styles.status, styles[statusVariant])}>{status}</span>,
          battery:
            device.battery_level == null ? (
              <span className={styles.muted}>—</span>
            ) : device.battery_level === 0 ? (
              <span className={classNames(styles.battery, styles.batteryMains)} title="Mains powered">
                <PlugIcon />
              </span>
            ) : (
              <span
                className={classNames(
                  styles.battery,
                  device.battery_level > BATTERY_LOW_THRESHOLD ? styles.batteryHigh : styles.batteryLow,
                )}
              >
                {device.battery_level > BATTERY_LOW_THRESHOLD ? <HighBatteryIcon /> : <LowBatteryIcon />}
                <span className={styles.batteryText}>{device.battery_level}%</span>
              </span>
            ),
          lastUpdate:
            validLastSeen && lastSeen ? (
              <span className={styles.lastUpdate}>{formatAbsoluteDateTime(lastSeen)}</span>
            ) : (
              <span className={styles.muted}>—</span>
            ),
          calibration: !calibration ? (
            <span className={styles.muted}>—</span>
          ) : (
            <span className={styles.calibration}>
              <span className={styles.calibrationHead}>
                <span className={classNames(styles.calibrationStatus, styles[calibration.variant])}>
                  {calibration.label}
                </span>
                {calibration.variant === "calibrated" &&
                  device.has_calibration_certificate &&
                  device.certificate_url && (
                    <a
                      className={styles.certificateIcon}
                      href={device.certificate_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open calibration certificate"
                    >
                      <CertificateIcon />
                    </a>
                  )}
              </span>
              <span className={styles.calibrationSub}>{calibration.sub}</span>
            </span>
          ),
          actions: (
            <div className="table-actions">
              <Button
                variant="link"
                aria-label="Edit device"
                className="table-actions__button"
                onClick={() => handleEditDevice(device.id)}
              >
                <EditIcon />
              </Button>

              <Button
                variant="link"
                aria-label="Recalibrate device"
                className="table-actions__button"
                onClick={() => handleRecalibrate(device.id)}
              >
                <RecalibrateIcon />
              </Button>

              <Button
                variant="link"
                aria-label="Delete device"
                className="table-actions__button"
                onClick={() => handleRemoveDevice(device.id)}
              >
                <TrashIcon />
              </Button>
            </div>
          ),
        };
      }),
    [items],
  );

  useEffect(() => {
    handleStatisticsRequest();
  }, [handleStatisticsRequest]);

  useEffect(() => {
    handleRequestList();
  }, [handleRequestList]);

  const reload = useCallback(() => {
    handleStatisticsRequest(true);
    handleRequestList(true);
  }, [handleStatisticsRequest, handleRequestList]);

  useRealtime(["devices"], reload, { portfolioId: Number(id) });

  // reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [assetValue, debouncedSearch, statusValue, typeValue]);

  // reset statistic when component unmount
  useEffect(() => {
    return () => {
      dispatch(resetPortfolioDeviceStatistic());
    };
  }, [dispatch]);

  // create device success logic
  useEffect(() => {
    if (lastCreatedDevEui) {
      dispatch(hideModal());
      dispatch(resetCreateDeviceState());
      handleRequestList();
      handleStatisticsRequest();
    }
  }, [lastCreatedDevEui, dispatch, handleRequestList, handleStatisticsRequest]);

  // update device success logic
  useEffect(() => {
    if (lastUpdatedDevEui) {
      dispatch(hideModal());
      dispatch(resetUpdateDeviceState());
      handleRequestList();
      handleStatisticsRequest();
    }
  }, [lastUpdatedDevEui, dispatch, handleRequestList, handleStatisticsRequest]);

  // remove device success logic
  useEffect(() => {
    if (lastRemovedDevEui) {
      dispatch(hideModal());
      dispatch(resetRemoveDeviceState());
      handleRequestList();
      handleStatisticsRequest();
    }
  }, [lastRemovedDevEui, dispatch, handleRequestList, handleStatisticsRequest]);

  // calibrate device success logic
  useEffect(() => {
    if (lastCalibratedDevEui) {
      dispatch(hideModal());
      dispatch(resetCalibrationState());
      handleRequestList();
      handleStatisticsRequest();
    }
  }, [lastCalibratedDevEui, dispatch, handleRequestList, handleStatisticsRequest]);

  return (
    <div className={styles.devices}>
      <div className={styles.stats}>
        {STAT_CARDS.map((card) => (
          <div key={card.key} className={styles.statCard}>
            <div className={styles.statContent}>
              <p className={styles.statValue}>
                {portfolioDeviceStatistic?.[card.key as keyof DeviceStatistics] ?? "–"}
              </p>
              <p className={styles.statTitle}>{card.title}</p>
            </div>

            {card.tone === "gray" ? (
              <span className={classNames(styles.statIcon, styles["statIcon--gray"])}>
                <AntennaIcon />
              </span>
            ) : (
              <span className={classNames(styles.statIcon, styles[`statIcon--${card.tone}`])}>
                <span className={styles.statDot} />
              </span>
            )}
          </div>
        ))}
      </div>

      <div className={styles.toolbar}>
        <SearchInput
          name="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search devices..."
          className={styles.search}
        />

        <Button variant="secondary" className={styles.toggle} onClick={() => setShowFilters((prev) => !prev)}>
          {showFilters ? "Close filters" : "Open filters"}
        </Button>

        <Button variant="primary" className={styles.add} onClick={handleAddDevice}>
          Add device
        </Button>

        {showFilters && (
          <div className={styles.filters}>
            <div className={styles.filtersWrap}>
              <AsyncSearchableSelect
                value={assetValue}
                variant="secondary"
                placeholder="All assets"
                selectedLabel={assetLabel}
                onChange={handleAssetChange}
                onSearch={handleAssetSearch}
                className={styles.filter}
              />

              <Select
                variant="secondary"
                options={TYPE_OPTIONS}
                value={typeValue}
                onChange={setTypeValue}
                placeholder="All types"
                className={styles.filter}
              />

              <Select
                variant="secondary"
                options={STATUS_OPTIONS}
                value={statusValue}
                onChange={setStatusValue}
                placeholder="All statuses"
                className={styles.filter}
              />
            </div>

            <Button form="circle" variant="white" className={styles.reload} onClick={handleClearFilters}>
              <ReloadIcon />
            </Button>

            <Button variant="white" className={styles.clearFilters} onClick={handleClearFilters}>
              Clear filters
            </Button>
          </div>
        )}
      </div>

      {!isLoading && tableData.length === 0 && (
        <div className={styles.notFound}>
          <NotFoundIcon />
          <p>No devices found</p>
        </div>
      )}

      {tableData.length > 0 && (
        <div className={styles.tableScroll}>
          <Table
            headers={HEADERS}
            data={tableData}
            pagination={{ total: Math.ceil(meta.total / meta.page_size) || 1, page, onPageChange: setPage }}
            className={styles.table}
          />
        </div>
      )}
    </div>
  );
};

export default PortfolioDevicesTab;
